import { useEffect, useState } from 'react';

import { calculateVerificationsAnalytics } from 'src/middlewares/utils';
import { useQuery } from 'src/modules/apollo';
import { useAppConfig } from 'src/modules/appConfig';
import { useCrossProductTransitionUrl } from 'src/modules/hooks/useCrossProductTransitionUrl';
import * as Schema from 'src/types/graphql';

import { useGetConfirmedRoles } from '../CareerHistory/data';

import type { Verified, Verifiable, VerificationsNudge } from './types';
import { processVerified, processVerifiable, processNudge } from './utils';

// Helper function to transform verifiable credentials' URLs
const transformVerifiableCredentials = async (
  processedVerifiable: ReturnType<typeof processVerifiable>['verifiable'],
  getCrossProductUrl: (url: string) => Promise<string>,
): Promise<any[] | undefined> => {
  if (!processedVerifiable) {
    return undefined;
  }

  return await Promise.all(
    processedVerifiable.map(async (category) => ({
      ...category,
      credentials: category.credentials
        ? await Promise.all(
            category.credentials.map(async (credential) => ({
              ...credential,
              url: await getCrossProductUrl(credential.url),
            })),
          )
        : null,
    })),
  );
};

export const useVerifications = () => {
  const { zone, language, locale } = useAppConfig();
  const languageLocale = `${language}-${locale}`;
  const getCrossProductUrl = useCrossProductTransitionUrl('seekpass');

  const { data, loading, error } = useQuery(Schema.GetVerificationsDocument, {
    variables: {
      zone,
      locale: languageLocale,
    },
  });

  const [verified, setVerified] = useState<Verified | undefined>(undefined);
  const [verifiedCredentialCount, setVerifiedCredentialCount] = useState<
    number | undefined
  >(undefined);
  const [expiryCount, setExpiryCount] = useState(0);
  const [verifiable, setVerifiable] = useState<Verifiable | undefined>(
    undefined,
  );
  const [verifiableCredentialCount, setVerifiableCredentialCount] = useState<
    number | undefined
  >(undefined);
  const [nudge, setNudge] = useState<VerificationsNudge | null>(null);
  const [showVerifications, setShowVerifications] = useState(false);

  useEffect(() => {
    const transformUrls = async () => {
      const verifications = data?.viewer?.profileVerifications;

      // Process verified - no URL transformation needed as verified credentials don't have URLs
      const {
        verified: processedVerified,
        verifiedCredentialCount: processedVerifiedCount,
        expiryCount: processedExpiryCount,
      } = processVerified(verifications?.verified);

      // Process verifiable
      const {
        verifiable: processedVerifiable,
        verifiableCredentialCount: processedVerifiableCount,
      } = processVerifiable(verifications?.verifiable);

      // Transform URLs in verifiable credentials
      const transformedVerifiable = await transformVerifiableCredentials(
        processedVerifiable,
        getCrossProductUrl,
      );

      const processedNudge = processNudge(verifications?.nudge);

      // Transform nudge credential URL if present
      let transformedNudge = processedNudge;
      if (
        processedNudge &&
        processedNudge.__typename === 'VerifiableCredentialNudge2' &&
        'credential' in processedNudge &&
        processedNudge.credential
      ) {
        const credential = processedNudge.credential;
        if (credential.url) {
          const url = await getCrossProductUrl(credential.url);

          transformedNudge = {
            ...processedNudge,
            credential: {
              ...processedNudge.credential,
              url,
            },
          };
        }
      }

      setVerified(processedVerified as Verified);
      setVerifiedCredentialCount(processedVerifiedCount ?? 0);
      setExpiryCount(processedExpiryCount ?? 0);
      setVerifiable(transformedVerifiable as Verifiable);
      setVerifiableCredentialCount(processedVerifiableCount ?? 0);
      setNudge(transformedNudge);

      // Update showVerifications
      setShowVerifications(
        Boolean(processedVerified && processedVerified.length > 0) ||
          Boolean(transformedVerifiable && transformedVerifiable.length > 0),
      );
    };

    transformUrls();
  }, [data, getCrossProductUrl]);

  // Calculate tracking props:
  const { confirmedRoles } = useGetConfirmedRoles();

  const verificationsTrackingProps = {
    ...calculateVerificationsAnalytics(verified, verifiable),
    profileCareerHistoryCount: confirmedRoles.length,
  };

  return {
    verified,
    verifiable,
    nudge,
    verifiedCredentialCount,
    verifiableCredentialCount,
    expiryCount,
    showVerifications,
    verificationsTrackingProps,
    loading,
    error,
  };
};
